import { getCsrfToken } from '../utils/helpers.js';
import {
    renderPaymentForm,
    submitPaymentForm,
    destroyPaymentForm
} from './payment.js';
import {
    trackReviewEvent,
    addPaymentInfo,
    purchase
} from '../utils/dataLayer.js';
import { getPayload as getAttributionPayload } from '../utils/attribution.js';
import { maxBookingDate } from '../utils/bookingDatePolicy.js';

/**
 * Booking Flow Component
 *
 * Manages the 5-step vacation rental booking process:
 *   1. Dates/Guests Selection → 2. Price Review → 3. Guest Details → 4. Payment → 5. Confirmation
 *
 * Integrates with:
 *   - Guesty Booking Engine API (quotes, reservations)
 *   - GuestyPay SDK (payment tokenization)
 *   - Flatpickr (date selection)
 *
 * State is managed via Alpine.js reactivity.
 * All API calls go through `/guesty/booking/*` backend endpoints.
 */

function isHtmlInputElement(value) {
    return (
        typeof HTMLInputElement !== 'undefined' &&
        value instanceof HTMLInputElement
    );
}

function toSafeString(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);

    // Never surface DOM objects like "[object HTMLInputElement]" in form fields.
    if (
        (typeof HTMLInputElement !== 'undefined' &&
            value instanceof HTMLInputElement) ||
        (typeof HTMLTextAreaElement !== 'undefined' &&
            value instanceof HTMLTextAreaElement)
    ) {
        return '';
    }

    return '';
}

function isCorruptedSessionValue(value) {
    return (
        value === '[object HTMLInputElement]' ||
        value === '[object HTMLTextAreaElement]'
    );
}

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function formatMarkdownLinksToHtml(value) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return '';

    const escaped = escapeHtml(text);
    const withLinks = escaped.replace(
        /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
        (_, label, url) =>
            `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline hover:no-underline">${label}</a>`
    );

    return withLinks.replace(/\r?\n/g, '<br>');
}

function normalizePriceComparisonRate(value) {
    const parsed = Number(value);

    if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
    }

    return parsed > 1 ? parsed / 100 : parsed;
}

function normalizePriceComparisonBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value > 0;
    if (typeof value !== 'string') return true;

    return !['false', 'no', '0', 'exclude', 'excluded'].includes(
        value.trim().toLowerCase()
    );
}

const PRICE_COMPARISON_ALLOWED_CHANNELS = new Map([
    ['airbnb', 'Airbnb'],
    ['booking.com', 'Booking.com'],
    ['bookingcom', 'Booking.com'],
    ['vrbo', 'Vrbo']
]);

function normalizePriceComparisonChannelName(value) {
    const key = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');

    return PRICE_COMPARISON_ALLOWED_CHANNELS.get(key) || '';
}

function normalizePriceComparisonChannels(channels) {
    if (!Array.isArray(channels)) return [];

    return channels
        .map((channel) => {
            const markupRate = normalizePriceComparisonRate(
                channel?.markupRate ?? channel?.rate ?? 0
            );
            const name = normalizePriceComparisonChannelName(
                channel?.channel || channel?.name || ''
            );
            const markupBase = String(
                channel?.markupBase || channel?.appliesTo || 'total'
            );

            return {
                channel: name,
                markupRate,
                markupBase: ['accommodation', 'fareFees', 'total'].includes(
                    markupBase
                )
                    ? markupBase
                    : 'total',
                includeInComparison: normalizePriceComparisonBoolean(
                    channel?.includeInComparison ?? channel?.included ?? true
                )
            };
        })
        .filter(
            (channel) =>
                channel.channel &&
                channel.includeInComparison &&
                channel.markupRate > 0
        );
}

export const bookingFlow = {
    init(options = {}) {
        return {
            // === Flow State ===
            step: options.step || 1, // 1: dates/guests, 2: review, 3: guest details, 4: payment, 5: confirmation
            loading: false,
            isSubmittingBooking: false,
            errors: {
                general: '',
                couponCode: '',
                payment: '',
                travelInsurance: '',
                petFee: '',
                upsells: ''
            },
            debug: !!options.debug,
            includeUpsells: !!options.includeUpsells,
            trackingContext: options.trackingContext || '',
            itemName: options.itemName || '',
            itemCategory: options.itemCategory || '',
            skipNextReviewTracking: !!options.skipNextReviewTracking,

            // === Listing & Booking Params ===
            listingId: options.listingId || null,
            checkIn: options.checkIn || '',
            checkOut: options.checkOut || '',
            adults: options.adults ?? 1,
            children: options.children ?? 0,
            infants: options.infants ?? 0,
            pets: options.pets ?? 0,
            couponCode: options.couponCode || '',

            // === Quote (from Guesty Booking Engine API) ===
            quoteId: null,
            entryId: options.entryId || null,
            checkoutSessionToken: options.checkoutSessionToken || '',
            quote: {},
            selectedUpsellFeeIds: [],
            upsellSelectionBeforeChange: null,
            paymentSchedule: null,
            availability: null,
            currency: 'USD',
            accountHandle: null,
            reviewRequestId: 0,
            quoteUpdateRequestId: 0,
            reviewTimeoutMs: 15000,
            checkoutDraftSaveTimer: null,
            defaultCancellationTitle:
                options.defaultCancellationTitle || 'Cancellation Policy',
            defaultCancellationText:
                options.defaultCancellationText ||
                'Review our cancellation policy for details.',
            cancellationNoticeTitle: '',
            cancellationNoticeText: '',
            cancellationLoading: false,
            cancellationResolved: false,
            priceComparisonEnabled: options.priceComparisonEnabled !== false,
            priceComparisonMarkupRate: normalizePriceComparisonRate(
                options.priceComparisonMarkupRate ?? 0.1278
            ),
            priceComparisonChannels: normalizePriceComparisonChannels(
                options.priceComparisonChannels ?? []
            ),
            priceComparisonMinimumSavings: Number(
                options.priceComparisonMinimumSavings ?? 1
            ),
            priceComparisonTitle:
                options.priceComparisonTitle ||
                'Book this stay with Avari for the best value',
            priceComparisonAvariLabel:
                options.priceComparisonAvariLabel || 'Avari',
            priceComparisonOtaLabel:
                options.priceComparisonOtaLabel || 'Other booking sites*',
            priceComparisonMessage: options.priceComparisonMessage || '',
            priceComparisonFootnote:
                options.priceComparisonFootnote ||
                '*Estimated third-party booking site average uses configured Airbnb, Vrbo, and Booking.com markups for this same stay. Actual third-party booking site pricing may vary.',

            // === Current User (Account Holder) ===
            currentUser: options.currentUser || null,

            // === Guest Checkout ===
            isGuestCheckout: false,
            get requiresAuthChoice() {
                const user = window.Alpine.store('user');
                return (
                    user.isFetched && !user.isLoggedIn && !this.isGuestCheckout
                );
            },
            emailExistsError: false,
            emailChecking: false,
            _emailCheckAbort: null,

            // === Guest Details (Step 3) ===
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            status: 'confirmed', // Always instant booking

            // === Payment (Step 4) ===
            paymentProviderId: null,
            paymentToken: null, // SDK booking credential for GuestyPay or a saved Guesty payment method ID
            paymentConfirmationToken: null,
            paymentBrand: '',
            paymentLast4: '',
            paymentSaved: false,
            paymentFormReady: false,
            savedPaymentMethods: [],
            selectedSavedMethod: null,
            paymentTokenType: null, // 'saved' when using an already-reusable Guesty method, 'tokenized' for a new card token

            // === Init Logic ===
            async loadSavedPaymentMethods() {
                if (!this.currentUser) return;

                try {
                    this.logEvent('payment.methods.load.start', {
                        hasUser: true
                    });
                    const response = await fetch(
                        '/guesty/booking/get-guest-payment-methods?listingId=' +
                            (this.listingId || ''),
                        {
                            headers: {
                                Accept: 'application/json',
                                'X-Requested-With': 'XMLHttpRequest'
                            }
                        }
                    );
                    const data = await response.json();
                    this.logEvent('payment.methods.load.response', {
                        success: !!data?.success,
                        status: response.status,
                        count: Array.isArray(data?.data)
                            ? data.data.length
                            : null,
                        ids: Array.isArray(data?.data)
                            ? data.data.map(
                                  (method) => method?._id || method?.id || null
                              )
                            : [],
                        hasPaymentToken: !!this.paymentToken,
                        hasSelectedSavedMethod: !!this.selectedSavedMethod
                    });

                    if (data.success && Array.isArray(data.data)) {
                        this.savedPaymentMethods = data.data;
                        this.logEvent('payment.methods.load.success', {
                            count: this.savedPaymentMethods.length
                        });

                        if (
                            this.savedPaymentMethods.length > 0 &&
                            this.paymentTokenType === 'saved' &&
                            this.paymentToken
                        ) {
                            const match = this.savedPaymentMethods.find(
                                (m) => m._id === this.paymentToken
                            );

                            if (match) {
                                this.selectedSavedMethod = match;
                                this.paymentBrand =
                                    match.brand ||
                                    match.cardType ||
                                    this.paymentBrand ||
                                    'Card';
                                this.paymentLast4 =
                                    match.last4 ||
                                    match.last4Digits ||
                                    this.paymentLast4 ||
                                    '****';
                            } else {
                                this.paymentToken = '';
                                this.paymentTokenType = null;
                                this.selectedSavedMethod = null;
                                this.paymentBrand = '';
                                this.paymentLast4 = '';
                                this.paymentSaved = false;
                            }
                        }

                        this.logEvent('payment.methods.render_state', {
                            count: this.savedPaymentMethods.length,
                            shouldShowSavedList:
                                this.savedPaymentMethods.length > 0 &&
                                !this.paymentToken &&
                                !this.selectedSavedMethod,
                            hasPaymentToken: !!this.paymentToken,
                            paymentTokenType: this.paymentTokenType || null,
                            hasSelectedSavedMethod: !!this.selectedSavedMethod
                        });
                    } else {
                        this.logEvent('payment.methods.load.unusable', {
                            success: !!data?.success,
                            errors: data?.errors || null
                        });
                    }
                } catch (e) {
                    this.logEvent('payment.methods.load.error', {
                        message: e?.message || String(e)
                    });
                }
            },

            async selectSavedMethod(method) {
                this.selectedSavedMethod = method;
                this.paymentToken = method._id;
                this.paymentTokenType = 'saved';
                this.paymentConfirmationToken = null;
                this.paymentBrand = method.brand || method.cardType || 'Card';
                this.paymentLast4 =
                    method.last4 || method.last4Digits || '****';
                this.paymentSaved = true;
                addPaymentInfo(this, 'credit_card');
                this.logEvent('payment.method.select', {
                    paymentMethodId: method?._id || null,
                    brand: this.paymentBrand,
                    last4: this.paymentLast4
                });

                // Clear any payment form data/errors
                this.errors.payment = '';

                // Saved cards are processor-scoped — ensure paymentProviderId is loaded
                // so the booking payload includes it (required by Guesty for saved card charges)
                if (!this.paymentProviderId) {
                    try {
                        await this.getPaymentProvider();
                    } catch (e) {
                        this.logEvent('payment.provider.prefetch.error', {
                            message: e?.message || String(e)
                        });
                        // Non-blocking: backend can still attempt without it
                    }
                }

                // Update session to reflect this selection
                this.saveSession();
            },

            clearSelectedMethod() {
                this.selectedSavedMethod = null;
                this.paymentToken = null;
                this.paymentTokenType = null;
                this.paymentConfirmationToken = null;
                this.paymentBrand = '';
                this.paymentLast4 = '';
                this.paymentSaved = false;
                this.logEvent('payment.method.clear');
            },

            continueAsGuest() {
                this.isGuestCheckout = true;
                this.saveSession();
                this.logEvent('guest_checkout.continue');
            },

            /**
             * Split fullName into [firstName, lastName].
             * Uses first token as firstName, remainder as lastName.
             */
            splitFullName(fullName) {
                if (!fullName) return ['', ''];
                const parts = fullName.trim().split(/\s+/);
                return [parts[0], parts.slice(1).join(' ')];
            },

            initSession() {
                try {
                    const session = JSON.parse(
                        sessionStorage.getItem('booking_flow_state') || '{}'
                    );

                    // Clean up known-corrupted persisted state.
                    if (
                        isCorruptedSessionValue(session.firstName) ||
                        isCorruptedSessionValue(session.lastName) ||
                        isCorruptedSessionValue(session.email) ||
                        isCorruptedSessionValue(session.phone) ||
                        isCorruptedSessionValue(session.note)
                    ) {
                        sessionStorage.removeItem('booking_flow_state');
                    }

                    const [userFirst, userLast] = this.splitFullName(
                        this.currentUser?.fullName
                    );
                    const hasAuthenticatedUser = !!(
                        this.currentUser && this.currentUser.email
                    );

                    if (hasAuthenticatedUser) {
                        this.firstName = toSafeString(userFirst);
                        this.lastName = toSafeString(userLast);
                        this.email = toSafeString(
                            this.currentUser?.email || ''
                        );
                        this.phone = toSafeString(
                            this.currentUser?.phone || ''
                        );
                    } else {
                        this.firstName = toSafeString(
                            session.firstName || userFirst
                        );
                        this.lastName = toSafeString(
                            session.lastName || userLast
                        );
                        this.email = toSafeString(
                            session.email || this.currentUser?.email || ''
                        );
                        this.phone = toSafeString(
                            session.phone || this.currentUser?.phone || ''
                        );
                    }

                    // Restore Note
                    if (session.note) {
                        this.note = toSafeString(session.note);
                    }

                    // Restore guest checkout state
                    if (session.isGuestCheckout) {
                        this.isGuestCheckout = true;
                    }

                    if (
                        Object.prototype.hasOwnProperty.call(
                            session,
                            'paymentToken'
                        ) ||
                        Object.prototype.hasOwnProperty.call(
                            session,
                            'paymentConfirmationToken'
                        ) ||
                        Object.prototype.hasOwnProperty.call(
                            session,
                            'paymentTokenType'
                        )
                    ) {
                        this.saveSession();
                    }

                    this.logEvent('session.restore', {
                        hasPaymentToken: !!session.paymentToken,
                        hasNote: !!session.note,
                        hasUser: !!this.currentUser
                    });
                } catch (e) {
                    this.logEvent('session.restore.error', {
                        message: e?.message || String(e)
                    });
                }
            },

            saveSession() {
                const state = {
                    firstName: toSafeString(this.firstName),
                    lastName: toSafeString(this.lastName),
                    email: toSafeString(this.email),
                    phone: toSafeString(this.phone),
                    note: toSafeString(this.note),
                    isGuestCheckout: this.isGuestCheckout
                };
                sessionStorage.setItem(
                    'booking_flow_state',
                    JSON.stringify(state)
                );
            },

            scheduleCheckoutDraftSave() {
                this.saveSession();
                if (!this.entryId || !this.quoteId) return;

                if (this.checkoutDraftSaveTimer) {
                    clearTimeout(this.checkoutDraftSaveTimer);
                }

                this.checkoutDraftSaveTimer = setTimeout(() => {
                    this.saveCheckoutDraft();
                }, 1200);
            },

            // === Checkout Form (Step 4) ===
            termsAccepted: false,
            insuranceOption: 'no',
            petFeeOption: 'no',
            autoSelectedPetFeeQuoteId: null,
            petFeeAppliedPetCount: 0,
            petFeeUserChanged: false,
            suppressNextPositivePetStoreSync: false,
            guestReviewDebounceTimer: null,
            note: '',

            // === Result ===
            reservationId: null,
            debugId: null,
            ...options,

            logEvent(event, data = {}) {
                if (!this.debug) {
                    return;
                }

                const payload = {
                    ts: new Date().toISOString(),
                    event,
                    debugId: this.debugId || null,
                    listingId: this.listingId || null,
                    quoteId: this.quoteId || null,
                    entryId: this.entryId || null,
                    ...data
                };
                try {
                    console.info('[BOOKING_FLOW]', payload);
                } catch {
                    // no-op
                }
            },

            emitReviewTrackingEvent() {
                if (this.skipNextReviewTracking) {
                    this.skipNextReviewTracking = false;
                    this.logEvent('tracking.review.skipped', {
                        trackingContext: this.trackingContext || null
                    });
                    return;
                }

                trackReviewEvent(this);
            },

            formatPrice(amount) {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: this.currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(amount);
            },

            formatPriceWithCents(amount) {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: this.currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(amount);
            },

            getOriginalAccommodationAmount() {
                const amount = Number(
                    this.quote?.originalAccommodation ??
                        this.quote?.money?.fareAccommodation ??
                        this.quote?.basePrice ??
                        0
                );

                return Number.isFinite(amount) ? Math.max(0, amount) : 0;
            },

            getAdjustedAccommodationAmount() {
                const original = this.getOriginalAccommodationAmount();
                const amount = Number(
                    this.quote?.adjustedAccommodation ??
                        this.quote?.money?.fareAccommodationAdjusted ??
                        original - Number(this.quote?.discount || 0)
                );

                return Number.isFinite(amount) ? Math.max(0, amount) : original;
            },

            getAccommodationDiscountAmount() {
                const explicitDiscount = Number(this.quote?.discount ?? NaN);
                if (Number.isFinite(explicitDiscount) && explicitDiscount > 0) {
                    return explicitDiscount;
                }

                return Math.max(
                    0,
                    this.getOriginalAccommodationAmount() -
                        this.getAdjustedAccommodationAmount()
                );
            },

            hasAccommodationDiscount() {
                return this.getAccommodationDiscountAmount() >= 0.01;
            },

            getAppliedPromotion() {
                const promotions = this.quote?.promotions;
                if (Array.isArray(promotions)) {
                    return (
                        promotions.find(
                            (promotion) =>
                                promotion && typeof promotion === 'object'
                        ) || null
                    );
                }

                if (promotions && typeof promotions === 'object') {
                    return promotions;
                }

                const invoicePromotion = Array.isArray(this.quote?.invoiceItems)
                    ? this.quote.invoiceItems.find(
                          (item) =>
                              String(item?.type || '').toUpperCase() ===
                                  'PROMOTION' ||
                              String(item?.normalType || '').toUpperCase() ===
                                  'PRO'
                      )
                    : null;

                return invoicePromotion
                    ? {
                          name:
                              invoicePromotion.description ||
                              invoicePromotion.title ||
                              'Promotion'
                      }
                    : null;
            },

            getAppliedDiscountLabel() {
                if (this.couponCode) {
                    return `Discount “${this.couponCode}”`;
                }

                const promotion = this.getAppliedPromotion();
                const promotionName = String(promotion?.name || '').trim();
                const discountType = String(promotion?.rule?.discountType || '')
                    .trim()
                    .toLowerCase();
                const discountAmount = Number(
                    promotion?.rule?.discountAmount ?? NaN
                );
                const percentSuffix =
                    discountType === 'percent' &&
                    Number.isFinite(discountAmount)
                        ? ` (${discountAmount}% off)`
                        : '';

                if (promotionName) {
                    return `${promotionName}${percentSuffix}`;
                }

                return 'Promotion';
            },

            getPriceComparisonRate() {
                const rates = this.getPriceComparisonChannels().map(
                    (channel) => channel.markupRate
                );

                if (rates.length > 0) {
                    return (
                        rates.reduce((sum, rate) => sum + rate, 0) /
                        rates.length
                    );
                }

                return normalizePriceComparisonRate(
                    this.priceComparisonMarkupRate
                );
            },

            getPriceComparisonMinimumSavings() {
                const parsed = Number(this.priceComparisonMinimumSavings);

                return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
            },

            getPriceComparisonOptionalExtrasTotal() {
                let total = 0;
                const travelInsurance = this.getTravelInsurance
                    ? this.getTravelInsurance()
                    : this.quote?.travelInsurance;

                if (travelInsurance?.selected) {
                    total += Number(travelInsurance.amount || 0);
                }

                if (typeof this.getSelectedUpsells === 'function') {
                    for (const upsell of this.getSelectedUpsells()) {
                        total += Number(upsell?.amount || 0);
                    }
                }

                return Number.isFinite(total) ? Math.max(0, total) : 0;
            },

            getPriceComparisonAvariTotal() {
                const quoteTotal = Number(this.quote?.total ?? this.total ?? 0);

                if (!Number.isFinite(quoteTotal) || quoteTotal <= 0) {
                    return 0;
                }

                return Math.max(
                    0,
                    quoteTotal - this.getPriceComparisonOptionalExtrasTotal()
                );
            },

            getPriceComparisonChannels() {
                return normalizePriceComparisonChannels(
                    this.priceComparisonChannels
                );
            },

            getPriceComparisonMarkupBase(channel = {}) {
                const avariTotal = this.getPriceComparisonAvariTotal();
                const accommodation = this.getAdjustedAccommodationAmount();
                const fees = Number(this.quote?.fees ?? 0);

                if (
                    channel.markupBase === 'accommodation' &&
                    Number.isFinite(accommodation) &&
                    accommodation > 0
                ) {
                    return Math.min(accommodation, avariTotal);
                }

                if (channel.markupBase === 'fareFees') {
                    const adjustedFees = Math.max(
                        0,
                        Number(fees || 0) -
                            this.getPriceComparisonOptionalExtrasTotal()
                    );
                    const fareFees = Number(accommodation || 0) + adjustedFees;

                    if (Number.isFinite(fareFees) && fareFees > 0) {
                        return Math.min(fareFees, avariTotal);
                    }
                }

                return avariTotal;
            },

            getPriceComparisonChannelOtaTotals() {
                const avariTotal = this.getPriceComparisonAvariTotal();

                if (avariTotal <= 0) {
                    return [];
                }

                return this.getPriceComparisonChannels()
                    .map(
                        (channel) =>
                            avariTotal +
                            this.getPriceComparisonMarkupBase(channel) *
                                channel.markupRate
                    )
                    .filter((total) => Number.isFinite(total) && total > 0);
            },

            getEstimatedPriceComparisonOtaTotal() {
                const avariTotal = this.getPriceComparisonAvariTotal();
                const channelTotals = this.getPriceComparisonChannelOtaTotals();

                if (channelTotals.length > 0) {
                    return (
                        channelTotals.reduce((sum, total) => sum + total, 0) /
                        channelTotals.length
                    );
                }

                const markupRate = this.getPriceComparisonRate();

                if (avariTotal <= 0 || markupRate <= 0) {
                    return 0;
                }

                return avariTotal * (1 + markupRate);
            },

            getEstimatedPriceComparisonSavings() {
                const savings =
                    this.getEstimatedPriceComparisonOtaTotal() -
                    this.getPriceComparisonAvariTotal();

                return Number.isFinite(savings) ? Math.max(0, savings) : 0;
            },

            hasPriceComparison() {
                const avariTotal = this.getPriceComparisonAvariTotal();
                const otaTotal = this.getEstimatedPriceComparisonOtaTotal();
                const savings = otaTotal - avariTotal;

                if (
                    !this.priceComparisonEnabled ||
                    avariTotal <= 0 ||
                    otaTotal <= avariTotal ||
                    savings <= 0
                ) {
                    return false;
                }

                return savings >= this.getPriceComparisonMinimumSavings();
            },

            formatPriceComparisonAmount(amount) {
                if (
                    Number(this.step) >= 4 &&
                    typeof this.formatPriceWithCents === 'function'
                ) {
                    return this.formatPriceWithCents(amount);
                }

                if (typeof this.formatPrice === 'function') {
                    return this.formatPrice(amount);
                }

                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: this.currency || 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(amount);
            },

            getPriceComparisonSavingsAmountText() {
                return `~${this.formatPriceComparisonAmount(this.getEstimatedPriceComparisonSavings())}`;
            },

            getPriceComparisonSavingsText() {
                const savingsAmount =
                    this.getPriceComparisonSavingsAmountText();
                const fallback = `Save ${savingsAmount} when you book direct with us`;
                const message =
                    typeof this.priceComparisonMessage === 'string'
                        ? this.priceComparisonMessage.trim()
                        : '';

                if (!message) {
                    return fallback;
                }

                return message.replace(
                    /\{\{\s*savings\s*\}\}|\{\s*savings\s*\}/gi,
                    savingsAmount
                );
            },

            formatDisplayDate(dateString) {
                if (!dateString || typeof dateString !== 'string') {
                    return 'Date TBD';
                }

                const [year, month, day] = dateString.split('-').map(Number);
                if (!year || !month || !day) {
                    return dateString;
                }

                return new Intl.DateTimeFormat('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                }).format(new Date(year, month - 1, day));
            },

            getCheckoutChargeAmount() {
                const dueNowAmount = Number(
                    this.paymentSchedule?.dueNowAmount ?? NaN
                );
                if (Number.isFinite(dueNowAmount) && dueNowAmount > 0) {
                    return dueNowAmount;
                }

                const quoteTotal = Number(this.quote?.total ?? NaN);
                if (Number.isFinite(quoteTotal) && quoteTotal > 0) {
                    return quoteTotal;
                }

                return 1;
            },

            applyQuoteData(data = {}) {
                const previousQuoteId = this.quoteId;
                this.quoteId = data.quoteId || this.quoteId;
                this.quote = data.quote || {};
                this.paymentSchedule = data.paymentSchedule || null;
                this.availability = data.availability ?? this.availability;
                this.accountHandle =
                    data.accountHandle || this.accountHandle || null;
                this.entryId = data.entryId || this.entryId || null;
                if (
                    previousQuoteId &&
                    this.quoteId &&
                    previousQuoteId !== this.quoteId
                ) {
                    this.autoSelectedPetFeeQuoteId = null;
                    this.petFeeUserChanged = false;
                }
                this.insuranceOption = this.quote?.travelInsurance?.selected
                    ? 'yes'
                    : 'no';
                this.petFeeOption = this.quote?.petFee?.selected ? 'yes' : 'no';
                const selectedAvailableUpsellIds = this.getAvailableUpsells()
                    .filter((upsell) => !!upsell?.selected && !!upsell?.feeId)
                    .map((upsell) => upsell.feeId);
                const selectedInvoiceUpsellIds = Array.isArray(
                    this.quote?.selectedUpsells
                )
                    ? this.quote.selectedUpsells
                          .filter((upsell) => !!upsell?.feeId)
                          .map((upsell) => upsell.feeId)
                    : [];
                this.syncSelectedUpsellFeeIds([
                    ...selectedAvailableUpsellIds,
                    ...selectedInvoiceUpsellIds
                ]);
                if (this.quote?.petFee?.selected) {
                    const quotedPetQuantity = Number(
                        this.quote.petFee.quantity || 0
                    );
                    const currentPetCount = Math.max(
                        0,
                        Math.min(2, Number(this.pets) || 0)
                    );
                    this.petFeeAppliedPetCount =
                        quotedPetQuantity > 0
                            ? quotedPetQuantity
                            : currentPetCount;
                } else {
                    this.petFeeAppliedPetCount = 0;
                }
                this.updateCancellationNoticeFromQuote();

                if (this.checkIn && this.checkOut && !this.quote.nightsCount) {
                    const start = new Date(this.checkIn);
                    const end = new Date(this.checkOut);
                    const nightsCount = Math.max(
                        1,
                        Math.round((end - start) / (1000 * 60 * 60 * 24))
                    );
                    this.quote.nightsCount = nightsCount;
                }

                if (this.quote.invoiceItems?.[0]?.currency) {
                    this.currency = this.quote.invoiceItems[0].currency;
                } else if (this.quote.money?.currency) {
                    this.currency = this.quote.money.currency;
                }

                this.maybeAutoSelectPetFee();
            },

            parseDateOnly(value) {
                if (typeof value !== 'string') return null;
                const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                if (!match) return null;

                const year = Number(match[1]);
                const month = Number(match[2]) - 1;
                const day = Number(match[3]);
                return new Date(year, month, day);
            },

            formatDateOnly(
                value,
                options = { month: 'short', day: 'numeric' }
            ) {
                const parsed = this.parseDateOnly(value);
                if (!parsed) return value || '';
                return parsed.toLocaleDateString('en-US', options);
            },

            formatStayDateRange(checkIn, checkOut) {
                if (!checkIn || !checkOut) return 'Select dates';
                const start = this.formatDateOnly(checkIn, {
                    month: 'short',
                    day: 'numeric'
                });
                const end = this.formatDateOnly(checkOut, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
                return `${start} - ${end}`;
            },

            getTravelInsurance() {
                return this.quote?.travelInsurance || null;
            },

            hasTravelInsuranceUpsell() {
                if (!this.getTravelInsurance()?.available) return false;
                if (!this.checkIn) return false;

                const checkInDate = this.parseDateOnly(this.checkIn);
                if (!checkInDate) return false;

                const now = new Date();
                const hoursUntilCheckIn =
                    (checkInDate - now) / (1000 * 60 * 60);
                return hoursUntilCheckIn >= 48;
            },

            getTravelInsuranceDocumentUrl() {
                const raw = this.getTravelInsurance()?.raw || {};
                const url =
                    raw.url ||
                    raw.pdfUrl ||
                    raw.documentUrl ||
                    raw.termsUrl ||
                    raw.policyUrl ||
                    null;
                return typeof url === 'string' && url.trim()
                    ? url.trim()
                    : null;
            },

            getTravelInsuranceDescriptionHtml() {
                const description =
                    this.getTravelInsurance()?.description ||
                    'Up to 100% back for reasons covered in terms of service';
                return formatMarkdownLinksToHtml(description);
            },

            getPetFee() {
                return this.quote?.petFee || null;
            },

            hasPetFeeUpsell() {
                return !!this.getPetFee()?.available;
            },

            getPetFeeDescription() {
                const petFee = this.getPetFee();
                const multiplier = String(
                    petFee?.multiplier || ''
                ).toUpperCase();
                const count = Math.max(0, Math.min(2, Number(this.pets) || 0));
                const amount = Number(petFee?.amount || 0);
                const normalizedUnitAmount = Number(petFee?.unitAmount ?? NaN);
                const unitPetAmount =
                    Number.isFinite(normalizedUnitAmount) &&
                    normalizedUnitAmount > 0
                        ? normalizedUnitAmount
                        : count > 0
                          ? amount / count
                          : amount;

                if (multiplier.includes('PER_PET_PER_NIGHT')) {
                    return count > 1
                        ? `${count} pets at ${this.formatPriceWithCents(unitPetAmount)} per pet per night.`
                        : `1 pet at ${this.formatPriceWithCents(unitPetAmount)} per pet per night.`;
                }
                if (multiplier.includes('PER_PET')) {
                    return count > 1
                        ? `${count} pets at ${this.formatPriceWithCents(unitPetAmount)} per pet.`
                        : `1 pet at ${this.formatPriceWithCents(unitPetAmount)} per pet.`;
                }
                if (multiplier.includes('PER_NIGHT')) {
                    return 'Charged per night.';
                }

                return petFee?.description || 'Charged once per stay.';
            },

            getAvailableUpsells() {
                return Array.isArray(this.quote?.availableUpsells)
                    ? this.quote.availableUpsells
                    : [];
            },

            hasAvailableUpsells() {
                return this.getAvailableUpsells().length > 0;
            },

            isUpsellSelected(feeId) {
                return !!feeId && this.selectedUpsellFeeIds.includes(feeId);
            },

            syncSelectedUpsellFeeIds(feeIds = []) {
                const selectedIds = [
                    ...new Set(
                        (Array.isArray(feeIds) ? feeIds : [])
                            .filter(
                                (feeId) =>
                                    feeId != null && String(feeId).trim() !== ''
                            )
                            .map((feeId) => String(feeId).trim())
                    )
                ];
                const selected = new Set(selectedIds);

                this.selectedUpsellFeeIds = selectedIds;

                if (Array.isArray(this.quote?.availableUpsells)) {
                    this.quote.availableUpsells =
                        this.quote.availableUpsells.map((upsell) => ({
                            ...upsell,
                            selected: selected.has(String(upsell?.feeId || ''))
                        }));
                }
            },

            getSelectedUpsells() {
                const selectedIds = new Set(this.selectedUpsellFeeIds);
                const selectedAvailable = this.getAvailableUpsells().filter(
                    (upsell) => selectedIds.has(upsell?.feeId)
                );

                if (selectedAvailable.length > 0) {
                    return selectedAvailable;
                }

                return Array.isArray(this.quote?.selectedUpsells)
                    ? this.quote.selectedUpsells
                    : [];
            },

            getUpsellDescriptionHtml(upsell) {
                return formatMarkdownLinksToHtml(upsell?.description || '');
            },

            formatUpsellPrice(upsell) {
                const amount = Number(upsell?.amount ?? 0);
                if (!Number.isFinite(amount) || amount <= 0) {
                    return '';
                }

                const multiplier = String(
                    upsell?.multiplier || ''
                ).toUpperCase();
                const nights = Number(this.quote?.nightsCount ?? 0);

                if (multiplier === 'PER_NIGHT' && nights > 1) {
                    const nightly = amount / nights;
                    return `${this.formatPriceWithCents(amount)} total (${this.formatPriceWithCents(nightly)}/night)`;
                }

                return this.formatPriceWithCents(amount);
            },

            getSelectedUpsellTotal() {
                let total = 0;
                const travelInsurance = this.getTravelInsurance();
                const petFee = this.getPetFee();

                if (travelInsurance?.selected) {
                    total += Number(travelInsurance.amount || 0);
                }
                if (petFee?.selected) {
                    total += Number(petFee.amount || 0);
                }
                for (const upsell of this.getSelectedUpsells()) {
                    total += Number(upsell?.amount || 0);
                }

                return total;
            },

            getTaxesAndFeesDisplayAmount() {
                const taxesAndFees =
                    Number(this.quote?.taxes || 0) +
                    Number(this.quote?.fees || 0);
                return Math.max(
                    0,
                    taxesAndFees - this.getSelectedUpsellTotal()
                );
            },

            async toggleUpsellSelection(feeId, checked) {
                if (!feeId) return;

                if (this.loading) {
                    const previousSelection = Array.isArray(
                        this.upsellSelectionBeforeChange
                    )
                        ? [...this.upsellSelectionBeforeChange]
                        : [...this.selectedUpsellFeeIds];
                    this.upsellSelectionBeforeChange = null;
                    this.syncSelectedUpsellFeeIds(previousSelection);
                    return;
                }

                const selected = new Set(this.selectedUpsellFeeIds);
                if (checked) {
                    selected.add(feeId);
                } else {
                    selected.delete(feeId);
                }

                const previousSelection = Array.isArray(
                    this.upsellSelectionBeforeChange
                )
                    ? [...this.upsellSelectionBeforeChange]
                    : [...this.selectedUpsellFeeIds];
                this.upsellSelectionBeforeChange = null;
                this.syncSelectedUpsellFeeIds([...selected]);
                await this.updateUpsellSelection(previousSelection);
            },

            async updateUpsellSelection(previousSelection = []) {
                if (!this.quoteId) return;

                this.loading = true;
                this.errors = { ...this.errors, upsells: '', general: '' };
                const requestId = ++this.quoteUpdateRequestId;

                try {
                    this.logEvent('upsells.update.start', {
                        selectedUpsellFeeIds: this.selectedUpsellFeeIds
                    });
                    const csrfToken = await getCsrfToken();
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    };

                    if (csrfToken) {
                        headers['X-CSRF-Token'] = csrfToken.value;
                    }

                    const body = {
                        quoteId: this.quoteId,
                        entryId: this.entryId || null,
                        accountHandle: this.accountHandle || null,
                        selectedUpsellFeeIds: this.selectedUpsellFeeIds
                    };

                    if (csrfToken) {
                        body[csrfToken.name] = csrfToken.value;
                    }

                    const response = await fetch(
                        '/guesty/booking/update-quote',
                        {
                            method: 'POST',
                            headers,
                            body: JSON.stringify(body)
                        }
                    );

                    const data = await response.json();
                    if (requestId !== this.quoteUpdateRequestId) {
                        this.logEvent('upsells.update.stale', { requestId });
                        return;
                    }

                    if (data.success) {
                        const requestedSelection = [
                            ...new Set(this.selectedUpsellFeeIds)
                        ];
                        this.applyQuoteData({
                            ...data.data,
                            accountHandle: this.accountHandle,
                            entryId: this.entryId
                        });
                        this.syncSelectedUpsellFeeIds(requestedSelection);
                        this.errors = { ...this.errors, upsells: '' };
                        this.logEvent('upsells.update.success', {
                            selectedUpsellFeeIds: this.selectedUpsellFeeIds,
                            total: this.quote?.total || null,
                            dueNowAmount:
                                this.paymentSchedule?.dueNowAmount ?? null
                        });
                    } else {
                        this.syncSelectedUpsellFeeIds(previousSelection);
                        this.errors = {
                            ...this.errors,
                            upsells:
                                data.errors?.upsells ||
                                data.errors?.general ||
                                'Unable to update optional extras.'
                        };
                        this.logEvent('upsells.update.error', {
                            errors: data.errors || null
                        });
                    }
                } catch (error) {
                    if (requestId !== this.quoteUpdateRequestId) {
                        return;
                    }
                    this.syncSelectedUpsellFeeIds(previousSelection);
                    this.errors = {
                        ...this.errors,
                        upsells:
                            'Unable to update optional extras. Please try again.'
                    };
                    this.logEvent('upsells.update.exception', {
                        message: error?.message || String(error)
                    });
                } finally {
                    if (requestId === this.quoteUpdateRequestId) {
                        this.loading = false;
                    }
                }
            },

            maybeAutoSelectPetFee() {
                const petCount = Math.max(
                    0,
                    Math.min(2, Number(this.pets) || 0)
                );
                if (!this.quoteId || this.petFeeUserChanged || petCount <= 0) {
                    return;
                }

                const petFee = this.getPetFee();
                if (!petFee?.available) {
                    return;
                }

                if (this.loading) {
                    setTimeout(() => this.maybeAutoSelectPetFee(), 100);
                    return;
                }

                const autoKey = `${this.quoteId}:${petCount}:${petFee.selected ? 'selected' : 'new'}`;
                if (this.autoSelectedPetFeeQuoteId === autoKey) {
                    return;
                }

                if (
                    petFee.selected &&
                    this.petFeeAppliedPetCount === petCount
                ) {
                    return;
                }

                this.autoSelectedPetFeeQuoteId = autoKey;
                queueMicrotask(() => {
                    const currentPetCount = Math.max(
                        0,
                        Math.min(2, Number(this.pets) || 0)
                    );
                    const currentPetFee = this.getPetFee();
                    if (
                        this.quoteId &&
                        !this.loading &&
                        currentPetCount > 0 &&
                        currentPetFee?.available &&
                        (!currentPetFee.selected ||
                            this.petFeeAppliedPetCount !== currentPetCount)
                    ) {
                        this.updatePetFeeSelection('yes', {
                            auto: true,
                            force: true
                        });
                    }
                });
            },

            scheduleGuestChangesReview() {
                this.pendingGuestsChange = true;

                if (this.guestReviewDebounceTimer) {
                    clearTimeout(this.guestReviewDebounceTimer);
                }

                this.guestReviewDebounceTimer = setTimeout(() => {
                    this.guestReviewDebounceTimer = null;
                    this.applyGuestChanges({ keepEditorOpen: true });
                }, 150);
            },

            syncPetsFromSearchStore(value) {
                const nextPets = Math.max(0, Math.min(2, Number(value) || 0));
                if (this.suppressNextPositivePetStoreSync && nextPets > 0) {
                    this.suppressNextPositivePetStoreSync = false;
                    if (this.$store?.search) {
                        this.$store.search.pets = 0;
                    }
                    return;
                }

                this.suppressNextPositivePetStoreSync = false;

                if (nextPets === this.pets) return;
                this.pets = nextPets;
                this.scheduleGuestChangesReview();
            },

            async applyGuestChanges(config = {}) {
                const petCount = Math.max(
                    0,
                    Math.min(2, Number(this.pets) || 0)
                );
                const hadSelectedPetFee =
                    this.petFeeOption === 'yes' || !!this.getPetFee()?.selected;
                const insuranceOptionBeforeReview = this.insuranceOption;
                let updatedPetFee = false;

                if (this.guestReviewDebounceTimer) {
                    clearTimeout(this.guestReviewDebounceTimer);
                    this.guestReviewDebounceTimer = null;
                }

                this.petFeeUserChanged = false;
                this.autoSelectedPetFeeQuoteId = null;
                this.pendingGuestsChange = false;
                if (!config.keepEditorOpen) {
                    this.showGuestEditor = false;
                }

                if (!this.checkIn || !this.checkOut || !this.listingId) {
                    return;
                }

                try {
                    await this.review();
                    await this.restoreTravelInsuranceAfterReview(
                        insuranceOptionBeforeReview
                    );

                    if (!this.quoteId || !this.hasPetFeeUpsell()) {
                        return;
                    }

                    if (petCount > 0) {
                        updatedPetFee = true;
                        await this.updatePetFeeSelection('yes', {
                            auto: true,
                            force: true
                        });
                        return;
                    }

                    if (hadSelectedPetFee || this.getPetFee()?.selected) {
                        updatedPetFee = true;
                        await this.updatePetFeeSelection('no', {
                            auto: true,
                            force: true
                        });
                    }
                } finally {
                    if (!updatedPetFee) {
                        this.loading = false;
                    }
                }
            },

            async declinePetFee() {
                if (this.loading) return;

                const insuranceOptionBeforeReview = this.insuranceOption;

                this.loading = true;

                if (this.guestReviewDebounceTimer) {
                    clearTimeout(this.guestReviewDebounceTimer);
                    this.guestReviewDebounceTimer = null;
                }

                this.petFeeOption = 'no';
                this.petFeeUserChanged = true;
                this.autoSelectedPetFeeQuoteId = null;
                this.suppressNextPositivePetStoreSync = true;
                this.pets = 0;
                this.pendingGuestsChange = false;

                if (this.$store?.search) {
                    this.$store.search.pets = 0;
                }

                let updatedPetFee = false;

                try {
                    if (!this.checkIn || !this.checkOut || !this.listingId) {
                        if (this.quoteId) {
                            updatedPetFee = true;
                            await this.updatePetFeeSelection('no', {
                                force: true
                            });
                        }
                        return;
                    }

                    await this.review();
                    await this.restoreTravelInsuranceAfterReview(
                        insuranceOptionBeforeReview
                    );

                    if (this.quoteId && this.getPetFee()?.selected) {
                        updatedPetFee = true;
                        await this.updatePetFeeSelection('no', { force: true });
                    }
                } finally {
                    if (!updatedPetFee) {
                        this.loading = false;
                    }
                }
            },

            async acceptPetFee() {
                if (this.loading) return;

                const insuranceOptionBeforeReview = this.insuranceOption;

                this.loading = true;

                if (this.guestReviewDebounceTimer) {
                    clearTimeout(this.guestReviewDebounceTimer);
                    this.guestReviewDebounceTimer = null;
                }

                this.petFeeOption = 'yes';
                this.petFeeUserChanged = true;
                this.autoSelectedPetFeeQuoteId = null;
                this.suppressNextPositivePetStoreSync = false;

                if (Math.max(0, Math.min(2, Number(this.pets) || 0)) === 0) {
                    this.pets = 1;

                    if (this.$store?.search) {
                        this.$store.search.pets = 1;
                    }
                }

                this.pendingGuestsChange = false;

                let updatedPetFee = false;

                try {
                    if (!this.checkIn || !this.checkOut || !this.listingId) {
                        if (this.quoteId) {
                            updatedPetFee = true;
                            await this.updatePetFeeSelection('yes', {
                                force: true
                            });
                        }
                        return;
                    }

                    await this.review();
                    await this.restoreTravelInsuranceAfterReview(
                        insuranceOptionBeforeReview
                    );

                    if (this.quoteId && this.hasPetFeeUpsell()) {
                        updatedPetFee = true;
                        await this.updatePetFeeSelection('yes', {
                            force: true
                        });
                    }
                } finally {
                    if (!updatedPetFee) {
                        this.loading = false;
                    }
                }
            },

            async restoreTravelInsuranceAfterReview(previousOption) {
                if (previousOption !== 'yes') {
                    return;
                }

                if (!this.hasTravelInsuranceUpsell()) {
                    this.insuranceOption = 'no';
                    return;
                }

                if (this.getTravelInsurance()?.selected) {
                    this.insuranceOption = 'yes';
                    return;
                }

                await this.updateTravelInsuranceSelection('yes');
            },

            dateToYmdLocal(date) {
                if (!(date instanceof Date) || Number.isNaN(date.getTime()))
                    return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            },

            updateCancellationNoticeFromQuote() {
                let title =
                    this.defaultCancellationTitle || 'Cancellation Policy';
                let text =
                    this.defaultCancellationText ||
                    'Review our cancellation policy for details.';

                const quoteText =
                    typeof this.quote?.cancellationPolicySummary === 'string'
                        ? this.quote.cancellationPolicySummary.trim()
                        : '';

                if (quoteText) {
                    text = quoteText;
                }

                this.cancellationNoticeTitle = title;
                this.cancellationNoticeText = text;
                this.cancellationResolved = true;
                this.cancellationLoading = false;
            },

            setFallbackCancellationNotice() {
                this.cancellationNoticeTitle =
                    this.defaultCancellationTitle || 'Cancellation Policy';
                this.cancellationNoticeText =
                    this.defaultCancellationText ||
                    'Review our cancellation policy for details.';
                this.cancellationResolved = true;
                this.cancellationLoading = false;
            },

            async datepicker(el) {
                if (!isHtmlInputElement(el)) return;

                // Already initialized
                if (el._flatpickr) {
                    el._flatpickr.open();
                    return;
                }

                // Prevent double-init
                if (el.dataset.flatpickrLoading === '1') return;
                el.dataset.flatpickrLoading = '1';

                try {
                    const { default: flatpickr } = await import('flatpickr');

                    flatpickr(el, {
                        allowInput: false,
                        dateFormat: 'Y-m-d',
                        mode: 'range',
                        minDate: 'today',
                        maxDate: maxBookingDate(),
                        onChange: (selectedDates, dateStr) => {
                            if (selectedDates.length === 2) {
                                this.checkIn = this.dateToYmdLocal(
                                    selectedDates[0]
                                );
                                this.checkOut = this.dateToYmdLocal(
                                    selectedDates[1]
                                );
                                // Update the display value
                                el.value = dateStr;
                            } else if (selectedDates.length === 0) {
                                this.checkIn = '';
                                this.checkOut = '';
                                el.value = '';
                            }
                            // Ensure Alpine x-model sees the updated value
                            el.dispatchEvent(
                                new Event('input', { bubbles: true })
                            );
                            el.dispatchEvent(
                                new Event('change', { bubbles: true })
                            );
                        }
                    });
                    el._flatpickr?.open();
                } finally {
                    delete el.dataset.flatpickrLoading;
                }
            },

            /**
             * Check if guest details are complete.
             * Returns true if valid, false otherwise.
             * Sets error message if invalid.
             */
            validateGuestDetails() {
                if (
                    !this.firstName ||
                    !this.lastName ||
                    !this.email ||
                    !this.phone
                ) {
                    this.errors = {
                        guest: 'Please complete guest details first'
                    };
                    return false;
                }
                if (this.emailExistsError) {
                    this.errors = {
                        guest: 'This email is already associated with an account. Please sign in to continue.'
                    };
                    return false;
                }
                this.saveSession();
                return true;
            },

            /**
             * Check if the guest email belongs to an existing user.
             * Called on blur of the email field during guest checkout.
             */
            async checkGuestEmail() {
                if (!this.isGuestCheckout || this.currentUser) return;

                const email = (this.email || '').trim();
                if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    this.emailExistsError = false;
                    return;
                }

                if (this._emailCheckAbort) {
                    this._emailCheckAbort.abort();
                }

                const controller = new AbortController();
                this._emailCheckAbort = controller;
                this.emailChecking = true;

                try {
                    const csrfToken = await getCsrfToken();
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/json'
                    };
                    if (csrfToken) {
                        headers['X-CSRF-Token'] = csrfToken.value;
                    }

                    const response = await fetch(
                        '/guesty/booking/check-guest-email',
                        {
                            method: 'POST',
                            headers,
                            signal: controller.signal,
                            body: JSON.stringify({ email })
                        }
                    );
                    const data = await response.json();
                    this.emailExistsError = !!data?.data?.exists;
                } catch (e) {
                    if (e?.name !== 'AbortError') {
                        this.emailExistsError = false;
                    }
                } finally {
                    this.emailChecking = false;
                    if (this._emailCheckAbort === controller) {
                        this._emailCheckAbort = null;
                    }
                }
            },

            /**
             * Step 1 → 2: Validate dates, check availability, and fetch a pricing quote.
             */
            async review() {
                if (!this.listingId || !this.checkIn || !this.checkOut) {
                    this.errors = {
                        general:
                            'Please select dates and ensure listing is selected.'
                    };
                    this.setFallbackCancellationNotice();
                    return;
                }

                if (this._reviewAbortController) {
                    this._reviewAbortController.abort('superseded');
                }

                this.loading = true;
                this.errors = {};
                this.cancellationLoading = true;
                this.cancellationResolved = false;
                const requestId = ++this.reviewRequestId;
                const abortController = new AbortController();
                this._reviewAbortController = abortController;
                const timeoutId = setTimeout(() => {
                    abortController.abort('timeout');
                }, this.reviewTimeoutMs);

                try {
                    this.logEvent('review.start', {
                        checkIn: this.checkIn,
                        checkOut: this.checkOut,
                        adults: this.adults,
                        children: this.children,
                        infants: this.infants,
                        pets: this.pets,
                        hasCoupon: !!this.couponCode
                    });
                    // The review endpoint disables CSRF server-side, so do not
                    // fetch a token here. This can run on checkout page load and
                    // should not disturb Sprig auth forms rendering nearby.
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/json'
                    };
                    const reviewRequestToken = `${Date.now()}-${requestId}-${Math.random().toString(36).slice(2, 8)}`;
                    headers['X-Booking-Review-Request-Id'] = reviewRequestToken;

                    const buildReviewBody = (
                        couponCode = this.couponCode || null
                    ) =>
                        JSON.stringify({
                            listingId: this.listingId,
                            checkIn: this.checkIn,
                            checkOut: this.checkOut,
                            adults: this.adults,
                            children: this.children,
                            infants: this.infants,
                            pets: this.pets,
                            couponCode,
                            includeUpsells: this.includeUpsells,
                            selectedUpsellFeeIds: this.selectedUpsellFeeIds,
                            entryId: this.entryId || null,
                            checkoutSessionToken:
                                this.checkoutSessionToken || null,
                            marketingAttribution: getAttributionPayload()
                        });

                    let response = await fetch('/guesty/booking/review', {
                        method: 'POST',
                        headers,
                        signal: abortController.signal,
                        body: buildReviewBody()
                    });

                    let text = await response.text();
                    let data;
                    try {
                        data = JSON.parse(text);
                    } catch {
                        console.error('Failed to parse JSON response:', text);
                        throw new Error('Invalid server response');
                    }

                    let couponErrors = null;
                    if (
                        !data.success &&
                        data.stage === 'coupon' &&
                        this.couponCode
                    ) {
                        couponErrors = {
                            couponCode:
                                data.errors?.couponCode ||
                                data.errors?.general ||
                                'Invalid promo code, please try again.'
                        };

                        response = await fetch('/guesty/booking/review', {
                            method: 'POST',
                            headers,
                            signal: abortController.signal,
                            body: buildReviewBody(null)
                        });

                        text = await response.text();
                        try {
                            data = JSON.parse(text);
                        } catch {
                            console.error(
                                'Failed to parse JSON response:',
                                text
                            );
                            throw new Error('Invalid server response');
                        }
                    }

                    // Ignore stale responses
                    if (requestId !== this.reviewRequestId) {
                        this.logEvent('review.stale_response', { requestId });
                        return;
                    }

                    if (data.success) {
                        this.applyQuoteData(data.data);
                        this.saveCheckoutDraft();
                        if (couponErrors) {
                            this.errors = couponErrors;
                        }

                        this.logEvent('review.success', {
                            quoteId: this.quoteId,
                            total: this.quote?.total || null,
                            dueNowAmount:
                                this.paymentSchedule?.dueNowAmount ?? null,
                            available: this.availability?.available ?? null,
                            entryId: this.entryId || null,
                            accountHandle: this.accountHandle || null
                        });

                        if (this.availability && !this.availability.available) {
                            this.errors = {
                                general:
                                    this.availability.error ||
                                    'This listing is not available for the selected dates. Please try different dates.'
                            };
                            return;
                        }

                        if (couponErrors) {
                            this.errors = couponErrors;
                        }

                        this.step = 2; // Move to review step
                        this.emitReviewTrackingEvent();
                    } else {
                        if (requestId !== this.reviewRequestId) {
                            this.logEvent('review.stale_error', { requestId });
                            return;
                        }
                        this.logEvent('review.error', {
                            stage: data.stage || null,
                            errors: data.errors || null
                        });
                        if (data.stage === 'availability') {
                            this.errors = {
                                general:
                                    data.errors?.general ||
                                    data.errors?.availability ||
                                    'This listing is not available for the selected dates. Please try different dates.'
                            };
                        } else {
                            const hasErrorsObject =
                                data.errors &&
                                typeof data.errors === 'object' &&
                                Object.keys(data.errors).length > 0;
                            this.errors = hasErrorsObject
                                ? data.errors
                                : {
                                      general:
                                          'Failed to get pricing. Please try again.'
                                  };
                        }
                    }
                } catch (error) {
                    if (requestId !== this.reviewRequestId) {
                        return;
                    }
                    if (error?.name === 'AbortError') {
                        const isTimeout =
                            abortController.signal.reason === 'timeout';
                        if (isTimeout) {
                            this.errors = {
                                general:
                                    'Pricing request timed out. Please try again.'
                            };
                            this.setFallbackCancellationNotice();
                            this.logEvent('review.timeout', {
                                requestId,
                                timeoutMs: this.reviewTimeoutMs
                            });
                        }
                        return;
                    }
                    this.logEvent('review.exception', {
                        message: error?.message || String(error)
                    });
                    const msg =
                        error?.message ||
                        (typeof error === 'string'
                            ? error
                            : 'Network error. Please try again.');
                    this.errors = { general: msg };
                    this.setFallbackCancellationNotice();
                } finally {
                    clearTimeout(timeoutId);
                    if (this._reviewAbortController === abortController) {
                        this._reviewAbortController = null;
                    }
                    if (requestId === this.reviewRequestId) {
                        if (!this.cancellationResolved) {
                            this.setFallbackCancellationNotice();
                        }
                        this.loading = false;
                    }
                }
            },

            /**
             * Fire-and-forget: persist checkout draft (dates, guests, coupon) to Craft entry.
             * Called after review() succeeds so the entry stays in sync without slowing the quote flow.
             */
            async saveCheckoutDraft() {
                if (!this.entryId) return;

                try {
                    const csrfToken = await getCsrfToken();
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    };
                    if (csrfToken) {
                        headers['X-CSRF-Token'] = csrfToken.value;
                    }

                    fetch('/guesty/booking/save-checkout-draft', {
                        method: 'POST',
                        headers,
                        credentials: 'same-origin',
                        body: JSON.stringify({
                            entryId: this.entryId,
                            checkIn: this.checkIn,
                            checkOut: this.checkOut,
                            adults: this.adults,
                            children: this.children,
                            infants: this.infants,
                            pets: this.pets,
                            couponCode: this.couponCode || null,
                            quoteId: this.quoteId || null,
                            checkoutSessionToken:
                                this.checkoutSessionToken || null,
                            marketingAttribution: getAttributionPayload(),
                            firstName: this.firstName || null,
                            lastName: this.lastName || null,
                            email: this.email || null,
                            phone: this.phone || null,
                            note: this.note || null,
                            totalAmount: this.quote?.total ?? null,
                            currency: this.currency || 'USD'
                        })
                    }).catch(() => {});
                } catch {
                    // Draft persistence should never interrupt quote review.
                }
            },

            async updateCoupon() {
                if (!this.quoteId) return;

                this.loading = true;
                this.errors = {};
                const requestId = ++this.quoteUpdateRequestId;

                try {
                    this.logEvent('coupon.update.start', {
                        hasCoupon: !!this.couponCode
                    });
                    const csrfToken = await getCsrfToken();
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    };

                    if (csrfToken) {
                        headers['X-CSRF-Token'] = csrfToken.value;
                    }

                    const body = {
                        quoteId: this.quoteId,
                        couponCode: this.couponCode || null,
                        entryId: this.entryId || null,
                        accountHandle: this.accountHandle || null
                    };

                    if (csrfToken) {
                        body[csrfToken.name] = csrfToken.value;
                    }

                    const response = await fetch(
                        '/guesty/booking/update-quote',
                        {
                            method: 'POST',
                            headers,
                            body: JSON.stringify(body)
                        }
                    );

                    const data = await response.json();
                    if (requestId !== this.quoteUpdateRequestId) {
                        this.logEvent('coupon.update.stale', { requestId });
                        return;
                    }

                    if (data.success) {
                        this.applyQuoteData({
                            ...data.data,
                            accountHandle: this.accountHandle,
                            entryId: this.entryId
                        });
                        this.logEvent('coupon.update.success', {
                            total: this.quote?.total || null,
                            dueNowAmount:
                                this.paymentSchedule?.dueNowAmount ?? null
                        });
                    } else {
                        this.logEvent('coupon.update.error', {
                            errors: data.errors || null
                        });
                        this.errors = data.errors || {
                            couponCode: 'Invalid coupon code.'
                        };
                    }
                } catch (error) {
                    if (requestId !== this.quoteUpdateRequestId) {
                        return;
                    }
                    this.errors = {
                        general: 'Failed to update coupon. Please try again.'
                    };
                    this.logEvent('coupon.update.exception', {
                        message: error?.message || String(error)
                    });
                } finally {
                    if (requestId === this.quoteUpdateRequestId) {
                        this.loading = false;
                    }
                }
            },

            async updateTravelInsuranceSelection(option) {
                if (!this.quoteId) {
                    this.insuranceOption = option;
                    return;
                }

                const selected = option === 'yes';
                const previousOption = this.insuranceOption;
                const alreadySelected = !!this.getTravelInsurance()?.selected;

                this.insuranceOption = option;
                this.loading = true;
                this.errors = {
                    ...this.errors,
                    general: '',
                    travelInsurance: ''
                };

                if (alreadySelected === selected) {
                    this.loading = false;
                    return;
                }

                const requestId = ++this.quoteUpdateRequestId;

                try {
                    this.logEvent('travel_insurance.update.start', {
                        selected
                    });
                    const csrfToken = await getCsrfToken();
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    };

                    if (csrfToken) {
                        headers['X-CSRF-Token'] = csrfToken.value;
                    }

                    const body = {
                        quoteId: this.quoteId,
                        entryId: this.entryId || null,
                        accountHandle: this.accountHandle || null,
                        travelInsuranceSelected: selected
                    };

                    if (csrfToken) {
                        body[csrfToken.name] = csrfToken.value;
                    }

                    const response = await fetch(
                        '/guesty/booking/update-quote',
                        {
                            method: 'POST',
                            headers,
                            body: JSON.stringify(body)
                        }
                    );

                    const data = await response.json();
                    if (requestId !== this.quoteUpdateRequestId) {
                        this.logEvent('travel_insurance.update.stale', {
                            requestId
                        });
                        return;
                    }

                    if (data.success) {
                        this.applyQuoteData({
                            ...data.data,
                            accountHandle: this.accountHandle,
                            entryId: this.entryId
                        });
                        this.errors = { ...this.errors, travelInsurance: '' };
                        this.logEvent('travel_insurance.update.success', {
                            selected,
                            total: this.quote?.total || null,
                            dueNowAmount:
                                this.paymentSchedule?.dueNowAmount ?? null
                        });
                    } else {
                        this.insuranceOption = previousOption;
                        this.errors = {
                            ...this.errors,
                            travelInsurance:
                                data.errors?.travelInsurance ||
                                data.errors?.general ||
                                'Unable to update travel insurance.'
                        };
                        this.logEvent('travel_insurance.update.error', {
                            errors: data.errors || null
                        });
                    }
                } catch (error) {
                    if (requestId !== this.quoteUpdateRequestId) {
                        return;
                    }
                    this.insuranceOption = previousOption;
                    this.errors = {
                        ...this.errors,
                        travelInsurance:
                            'Unable to update travel insurance. Please try again.'
                    };
                    this.logEvent('travel_insurance.update.exception', {
                        message: error?.message || String(error)
                    });
                } finally {
                    if (requestId === this.quoteUpdateRequestId) {
                        this.loading = false;
                    }
                }
            },

            async updatePetFeeSelection(option, config = {}) {
                if (!this.quoteId) {
                    this.petFeeOption = option;
                    return;
                }

                const selected = option === 'yes';
                const previousOption = this.petFeeOption;
                const alreadySelected = !!this.getPetFee()?.selected;
                const petCount = Math.max(
                    0,
                    Math.min(2, Number(this.pets) || 0)
                );
                const quantityChanged =
                    selected &&
                    alreadySelected &&
                    this.petFeeAppliedPetCount !== petCount;

                if (!config.auto) {
                    this.petFeeUserChanged = true;
                }

                this.petFeeOption = option;
                this.loading = true;
                this.errors = { ...this.errors, general: '', petFee: '' };

                if (
                    alreadySelected === selected &&
                    !config.force &&
                    !quantityChanged
                ) {
                    this.loading = false;
                    return;
                }

                const requestId = ++this.quoteUpdateRequestId;

                try {
                    this.logEvent('pet_fee.update.start', {
                        selected,
                        pets: this.pets,
                        auto: !!config.auto
                    });
                    const csrfToken = await getCsrfToken();
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    };

                    if (csrfToken) {
                        headers['X-CSRF-Token'] = csrfToken.value;
                    }

                    const body = {
                        quoteId: this.quoteId,
                        entryId: this.entryId || null,
                        accountHandle: this.accountHandle || null,
                        petFeeSelected: selected,
                        pets: petCount,
                        selectedUpsellFeeIds: this.selectedUpsellFeeIds
                    };

                    if (csrfToken) {
                        body[csrfToken.name] = csrfToken.value;
                    }

                    const response = await fetch(
                        '/guesty/booking/update-quote',
                        {
                            method: 'POST',
                            headers,
                            body: JSON.stringify(body)
                        }
                    );

                    const data = await response.json();
                    if (requestId !== this.quoteUpdateRequestId) {
                        this.logEvent('pet_fee.update.stale', { requestId });
                        return;
                    }

                    if (data.success) {
                        this.applyQuoteData({
                            ...data.data,
                            accountHandle: this.accountHandle,
                            entryId: this.entryId
                        });
                        this.petFeeAppliedPetCount = selected ? petCount : 0;
                        this.errors = { ...this.errors, petFee: '' };
                        this.logEvent('pet_fee.update.success', {
                            selected,
                            total: this.quote?.total || null,
                            dueNowAmount:
                                this.paymentSchedule?.dueNowAmount ?? null
                        });
                    } else {
                        this.petFeeOption = previousOption;
                        this.errors = {
                            ...this.errors,
                            petFee:
                                data.errors?.petFee ||
                                data.errors?.general ||
                                'Unable to update pet fee.'
                        };
                        this.logEvent('pet_fee.update.error', {
                            errors: data.errors || null
                        });
                    }
                } catch (error) {
                    if (requestId !== this.quoteUpdateRequestId) {
                        return;
                    }
                    this.petFeeOption = previousOption;
                    this.errors = {
                        ...this.errors,
                        petFee: 'Unable to update pet fee. Please try again.'
                    };
                    this.logEvent('pet_fee.update.exception', {
                        message: error?.message || String(error)
                    });
                } finally {
                    if (requestId === this.quoteUpdateRequestId) {
                        this.loading = false;
                    }
                }
            },

            async getPaymentProvider() {
                if (!this.listingId) {
                    this.errors = { payment: 'Listing ID is required.' };
                    return null;
                }

                if (this.paymentProviderId) {
                    return this.paymentProviderId; // Already loaded
                }

                try {
                    this.logEvent('payment.provider.start');
                    const response = await fetch(
                        `/guesty/booking/payment-provider?listingId=${this.listingId}`,
                        {
                            method: 'GET',
                            headers: {
                                Accept: 'application/json',
                                'X-Requested-With': 'XMLHttpRequest'
                            }
                        }
                    );

                    const data = await response.json();

                    if (data.success && data.data?.paymentProviderId) {
                        this.paymentProviderId = data.data.paymentProviderId;
                        this.logEvent('payment.provider.success');
                        return this.paymentProviderId;
                    } else {
                        throw new Error(
                            data.errors?.payment ||
                                'Failed to retrieve payment provider.'
                        );
                    }
                } catch (error) {
                    this.logEvent('payment.provider.error', {
                        message: error?.message || String(error)
                    });
                    this.errors = {
                        payment:
                            error.message ||
                            'Failed to load payment provider. Please try again.'
                    };
                    throw error;
                }
            },

            async initPaymentForm() {
                this.errors = { payment: '' };
                this.paymentFormReady = false;

                try {
                    const paymentProviderId = await this.getPaymentProvider();
                    if (!paymentProviderId) {
                        return;
                    }

                    const amount = this.getCheckoutChargeAmount();
                    const currency = this.currency || 'USD';

                    this.logEvent('payment.form.init.start', {
                        amount,
                        currency
                    });

                    await renderPaymentForm({
                        containerId: 'guesty-payment-form',
                        paymentProviderId: paymentProviderId,
                        listingId: this.listingId,
                        amount: amount,
                        currency: currency,
                        onValidChange: (isValid) => {
                            this.logEvent('payment.form.validity', { isValid });
                        }
                    });

                    this.paymentFormReady = true;
                    this.logEvent('payment.form.init.success');
                } catch (error) {
                    this.logEvent('payment.form.init.error', {
                        message: error?.message || String(error)
                    });
                    this.errors = {
                        payment:
                            error.message ||
                            'Failed to load payment form. Please try again.'
                    };
                }
            },

            async submitPayment() {
                if (!this.paymentFormReady) {
                    this.errors = {
                        payment:
                            'Payment form not ready. Please wait or refresh the page.'
                    };
                    return;
                }

                this.errors = { payment: '' };
                this.loading = true;

                try {
                    if (!this.debugId) {
                        this.debugId = `bf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                    }
                    // Construct guest payload for payment tokenization
                    // CRITICAL requirement: Use logged-in user details if available, regardless of guest form
                    const [tokenFirst, tokenLast] = this.splitFullName(
                        this.currentUser?.fullName
                    );
                    const tokenizationGuest =
                        this.currentUser && this.currentUser.email
                            ? {
                                  firstName: tokenFirst,
                                  lastName: tokenLast,
                                  email: this.currentUser.email,
                                  phone: this.currentUser.phone || this.phone // Fallback to form phone if missing in profile
                              }
                            : {
                                  firstName: this.firstName,
                                  lastName: this.lastName,
                                  email: this.email,
                                  phone: this.phone
                              };

                    const payload = {
                        listingId: this.listingId,
                        quoteId: this.quoteId,
                        paymentProviderId: this.paymentProviderId || undefined,
                        // Add amount and currency to payload (SDK requirement)
                        amount: this.getCheckoutChargeAmount(),
                        currency: this.currency || 'USD',
                        guest: tokenizationGuest,
                        address: {},
                        threeDS: {
                            amount: this.getCheckoutChargeAmount(),
                            currency: this.currency || 'USD',
                            successURL: this.buildThreeDSUrl('success'),
                            failureURL: this.buildThreeDSUrl('failure')
                        }
                        // We do not send `reuse` here, as that would override the user's choice in the iframe's internal checkbox.
                        // The SDK should return `saveForFutureUse: true` if the user checked the box.
                    };

                    this.logEvent('payment.submit.start', {
                        hasUser: !!this.currentUser,
                        amount: payload.amount,
                        currency: payload.currency
                    });

                    const result = await submitPaymentForm(payload);
                    this.logEvent('payment.submit.result', {
                        hasId: !!(result?.id || result),
                        brand: result?.brand || null,
                        last4: result?.last4 || null,
                        saveForFutureUse: !!result?.saveForFutureUse,
                        resultKeys: result?.resultKeys || []
                    });

                    const paymentMethodId = result?.id || result;
                    const confirmationToken = result?.confirmationToken || null;
                    const brand = result?.brand || '';
                    const last4 = result?.last4 || '';

                    if (
                        paymentMethodId &&
                        typeof paymentMethodId === 'string' &&
                        paymentMethodId.trim().length > 0
                    ) {
                        this.paymentToken = paymentMethodId;
                        this.paymentTokenType = 'tokenized';
                        this.paymentConfirmationToken = confirmationToken;
                        this.selectedSavedMethod = null;
                        this.paymentBrand = brand;
                        this.paymentLast4 = last4;

                        // If we don't have good metadata, try to fetch it from the backend
                        if (
                            this.paymentBrand === 'Card' ||
                            this.paymentLast4 === '****'
                        ) {
                            try {
                                this.logEvent('payment.method.details.start');
                                const response = await fetch(
                                    `/guesty/booking/get-payment-method?id=${paymentMethodId}&listingId=${this.listingId}`,
                                    {
                                        headers: {
                                            Accept: 'application/json',
                                            'X-Requested-With': 'XMLHttpRequest'
                                        }
                                    }
                                );
                                const data = await response.json();

                                if (data.success && data.data) {
                                    this.paymentBrand =
                                        data.data.brand || this.paymentBrand;
                                    this.paymentLast4 =
                                        data.data.last4 || this.paymentLast4;
                                    this.logEvent(
                                        'payment.method.details.success',
                                        {
                                            brand: this.paymentBrand,
                                            last4: this.paymentLast4
                                        }
                                    );
                                }
                            } catch (e) {
                                this.logEvent('payment.method.details.error', {
                                    message: e?.message || String(e)
                                });
                            }
                        }

                        this.paymentSaved = true;
                        addPaymentInfo(this, 'credit_card');
                        this.saveSession();

                        await new Promise((resolve) => setTimeout(resolve, 50));

                        this.closePaymentModal();

                        this.logEvent('payment.submit.success', {
                            hasPaymentToken: !!this.paymentToken,
                            brand: this.paymentBrand,
                            last4: this.paymentLast4,
                            paymentSaved: this.paymentSaved
                        });
                    } else {
                        this.logEvent('payment.submit.invalid_id', {
                            paymentMethodIdType: typeof paymentMethodId
                        });
                        this.errors = {
                            payment:
                                'Payment processing did not return a valid ID. Please try again.'
                        };
                    }
                } catch (error) {
                    this.logEvent('payment.submit.exception', {
                        message: error?.message || String(error)
                    });
                    this.errors = {
                        payment:
                            error.message ||
                            'Payment processing failed. Please try again.'
                    };
                } finally {
                    this.loading = false;
                }
            },

            attemptClosePaymentModal() {
                if (this.loading) {
                    return;
                }

                if (this.paymentFormReady) {
                    const confirmed = window.confirm(
                        'Close the payment form? Any details you have entered will be lost.'
                    );
                    if (!confirmed) {
                        return;
                    }
                }

                this.closePaymentModal();
            },

            closePaymentModal() {
                destroyPaymentForm();
                this.paymentFormReady = false;

                if (window.Alpine?.store('modals')) {
                    window.Alpine.store('modals').close('payment-modal');
                }
            },

            async create() {
                if (this.isSubmittingBooking) {
                    return;
                }

                if (!this.quoteId) {
                    this.errors = {
                        general: 'Please complete the review step first.'
                    };
                    return;
                }

                if (!this.validateGuestDetails()) {
                    this.errors = {
                        guest: 'Please fill in all guest details.'
                    };
                    return;
                }

                if (!this.paymentToken) {
                    this.errors = {
                        general:
                            'Please add a payment method before continuing.'
                    };
                    return;
                }

                if (this.requiresAuthChoice) {
                    window.Alpine.store('modals').open('sign-in-modal');
                    return;
                }

                this.reservationId = null;
                this.isSubmittingBooking = true;
                this.loading = true;
                this.errors = {};

                try {
                    this.debugId = `bf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                    this.logEvent('booking.submit.start', {
                        hasPaymentToken: !!this.paymentToken,
                        paymentTokenType: this.paymentTokenType || null,
                        hasConfirmationToken: !!this.paymentConfirmationToken
                    });
                    const csrfToken = await getCsrfToken();
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    };

                    if (csrfToken) {
                        headers['X-CSRF-Token'] = csrfToken.value;
                    }

                    const isSavedPaymentMethod =
                        this.paymentTokenType === 'saved';
                    const paymentPayload = isSavedPaymentMethod
                        ? {
                              initialPaymentMethodId: this.paymentToken
                          }
                        : {
                              initialPaymentMethodId: this.paymentToken,
                              confirmationToken:
                                  this.paymentConfirmationToken || undefined
                          };
                    const body = {
                        quoteId: this.quoteId,
                        listingId: this.listingId,
                        accountHandle: this.accountHandle,
                        entryId: this.entryId,
                        paymentProviderId: this.paymentProviderId || undefined,
                        paymentTokenType: this.paymentTokenType || null,
                        firstName: this.firstName,
                        lastName: this.lastName,
                        email: this.email,
                        phone: this.phone,
                        pets: this.pets,
                        status: 'confirmed',
                        note: this.note || null,
                        marketingAttribution: getAttributionPayload(),
                        payment: paymentPayload,
                        debugId: this.debugId
                    };

                    const response = await fetch('/guesty/booking/create', {
                        method: 'POST',
                        headers,
                        credentials: 'same-origin',
                        body: JSON.stringify(body)
                    });
                    const data = await response.json();
                    this.logEvent('booking.submit.response', {
                        status: response.status,
                        success: !!data.success,
                        stage: data.stage || null,
                        hasErrors: !!data.errors
                    });

                    if (
                        (response.status === 401 || data.stage === 'auth') &&
                        !this.isGuestCheckout
                    ) {
                        window.Alpine.store('modals').open('sign-in-modal');
                        this.errors = data.errors || {
                            general: 'You must be signed in to reserve a stay.'
                        };
                        this.logEvent('booking.submit.unauthorized', {
                            status: response.status,
                            stage: data.stage || null
                        });
                        return;
                    }

                    if (data.success) {
                        try {
                            // Extract reservation ID safely (check root, data, reservation object)
                            const rId =
                                data.data?._id ||
                                data.data?.reservationId ||
                                data.data?.reservation?._id ||
                                data.data?.reservation?.reservationId ||
                                data.data?.id ||
                                null;

                            this.logEvent('booking.reservation.extract', {
                                reservationId: rId || null
                            });
                            this.reservationId = rId;
                        } catch (e) {
                            this.logEvent('booking.reservation.extract.error', {
                                message: e?.message || String(e)
                            });
                        }

                        this.step = 5; // Move to confirmation

                        this.logEvent('booking.submit.success', {
                            reservationId: this.reservationId || null
                        });

                        if (this.reservationId) {
                            try {
                                purchase(this, this.reservationId);
                            } catch (e) {
                                this.logEvent('booking.purchase.event.error', {
                                    message: e?.message || String(e)
                                });
                            }

                            // Clear session storage to prevent old data (like notes) from leaking into new bookings
                            sessionStorage.removeItem('booking_flow_state');

                            setTimeout(() => {
                                const params = new URLSearchParams({
                                    reservationId: this.reservationId
                                });
                                if (this.isGuestCheckout) {
                                    params.set('guest', '1');
                                }
                                window.location.href = `/checkout/confirmation?${params.toString()}`;
                            }, 2000);
                        } else {
                            this.errors = {
                                general:
                                    'Reservation created but confirmation failed. Please check your reservations.'
                            };
                        }
                    } else {
                        if (data.stage === 'guest_email_conflict') {
                            this.emailExistsError = true;
                            this.errors = {
                                guest:
                                    data.errors?.guest ||
                                    'This email is already associated with an account. Please sign in to continue.'
                            };
                            this.logEvent(
                                'booking.submit.guest_email_conflict'
                            );
                            return;
                        }

                        this.logEvent('booking.submit.failed', {
                            errors: data.errors || null
                        });
                        const errorMessage =
                            data.errors?.general ||
                            data.errors?.quote ||
                            data.errors?.reservation ||
                            data.errors?.payment ||
                            data.errors?.message ||
                            (typeof data.errors === 'string'
                                ? data.errors
                                : null) ||
                            'Failed to create reservation. Please try again.';

                        this.errors = { general: errorMessage };

                        // If the error might be related to the payment token (e.g. 500 or 400),
                        // clear it to force the user to re-enter/re-tokenize.
                        // This prevents getting stuck with a stale/consumed token.
                        if (this.paymentToken) {
                            this.logEvent('booking.payment.clear_stale_token');
                            this.paymentToken = null;
                            this.paymentTokenType = null;
                            this.paymentConfirmationToken = null;
                            this.selectedSavedMethod = null;
                            this.paymentSaved = false;
                            this.saveSession(); // Update session to remove the bad token
                        }
                    }
                } catch (error) {
                    this.errors = {
                        general: 'Network error. Please try again.'
                    };
                    this.logEvent('booking.submit.exception', {
                        message: error?.message || String(error)
                    });
                } finally {
                    if (!this.reservationId) {
                        this.isSubmittingBooking = false;
                        this.loading = false;
                    }
                }
            },

            buildThreeDSUrl(result) {
                try {
                    const url = new URL(window.location.href);
                    url.searchParams.set('payment3ds', result);
                    return url.toString();
                } catch {
                    return window.location.href;
                }
            },

            initAuthListeners() {
                const onAuthSuccess = async () => {
                    const userStore = window.Alpine.store('user');

                    // Wait for the user store to finish refreshing (triggered by sign-in/sign-up handlers)
                    await new Promise((resolve) => {
                        if (userStore.isLoggedIn) {
                            resolve();
                            return;
                        }
                        const unwatch = window.Alpine.effect(() => {
                            if (userStore.isLoggedIn) {
                                unwatch();
                                resolve();
                            }
                        });
                    });

                    this.currentUser = {
                        fullName: userStore.fullName,
                        email: userStore.email,
                        phone: userStore.primaryPhoneNumber
                    };
                    this.emailExistsError = false;
                    this.isGuestCheckout = false;

                    // Pre-fill guest details if still empty
                    const [first, last] = this.splitFullName(
                        userStore.fullName
                    );
                    if (!this.firstName) this.firstName = toSafeString(first);
                    if (!this.lastName) this.lastName = toSafeString(last);
                    if (!this.email) this.email = toSafeString(userStore.email);
                    if (!this.phone)
                        this.phone = toSafeString(userStore.primaryPhoneNumber);

                    this.loadSavedPaymentMethods();
                };

                if (window.htmx) {
                    window.htmx.on('loginSucceeded', onAuthSuccess);
                    window.htmx.on('signupSucceeded', onAuthSuccess);
                }
            },

            init() {
                this.initSession();
                this.initAuthListeners();
                if (typeof this.$watch === 'function') {
                    ['firstName', 'lastName', 'email', 'phone', 'note'].forEach(
                        (key) => {
                            this.$watch(key, () =>
                                this.scheduleCheckoutDraftSave()
                            );
                        }
                    );
                }
                if (this.currentUser) {
                    this.loadSavedPaymentMethods();
                }
            }
        };
    }
};

if (typeof window !== 'undefined') {
    window.bookingFlow = bookingFlow;
}
